import { useState } from 'react';
import useGetDrivers from '../../../API/hooks/drivers/useGetDrivers';
import { FilterConfig } from '../../common/DropdownFilter/types';
import { queryFilters } from '../../../utils/queryFilters';
import { DriverDetails as DriverType } from '../../../API/types/Drivers';
import S from './AddDriverToVehicleStyles';
import Driver from './Driver';
import { Button } from '../../ui/Button/Button';
import { IconKeys } from '../../common/Icon/IconKeys';
import Icon from '../../common/Icon/Icon';
import { generatePreviewFullName } from '../../../pages/Drivers/Modals/helpers';
import Tags from '../../common/Tags';
import DropdownFilter from '../../common/DropdownFilter/DropdownFilter';
import useDriverThumbnail from '../../../theme/useDriverThumbnail';
import { DriverAuthorizationRecord } from '../../../API/types/Vehicles';
import usePostAuthorizedDriver from '../../../API/hooks/vehicles/usePostAuthorizedDriver';

type Props = {
  onCancel: () => void;
  fleetId: string;
  vehicleId: string;
  authorizedDrivers: DriverAuthorizationRecord[];
};

const SEARCH_CONFIG = [
  {
    type: 'sortBy',
    display: 'radio',
    options: [
      { score_DESC: 'Score: most' },
      { score_ASC: 'Score: least' },
    ],
  },
] as FilterConfig[];

function AddDriverToVehicle({
  onCancel,
  vehicleId,
  fleetId,
  authorizedDrivers,
}: Props): JSX.Element {
  const driverThumbnail = useDriverThumbnail();
  const [filters, setFilters] = useState<Record<string, string[]>>({});

  const { data, isLoading, isFetching } = useGetDrivers(fleetId, queryFilters(filters));

  const authorizedDriversIds = authorizedDrivers.map((d: DriverAuthorizationRecord) => d.driverId);

  const availableDrivers = data?.results.filter((d: DriverType) => !authorizedDriversIds.includes(d._id)) || [];

  const [selectedDriver, setSelectedDriver] = useState<DriverType | null>(null);
  const [assignError, setAssignError] = useState('');

  const onSubmitSuccess = (): void => {
    setSelectedDriver(null);
    setAssignError('');
    onCancel();
  };
  const onSubmitFail = (error: string): void => {
    setAssignError(error);
  };

  const { mutate } = usePostAuthorizedDriver(onSubmitSuccess, onSubmitFail);

  const handleDriverAssignment = (): void => {
    if (selectedDriver?._id && fleetId && vehicleId) {
      mutate({ driverId: selectedDriver?._id, fleetId, vehicleId });
    } else {
      setAssignError('No driverId/fleedId/VehicleId');
    }
  };

  const handleDriverSelection = (driver: DriverType): void => {
    if (driver.faceData) {
      setSelectedDriver(() => driver);
    }
  };

  return (
    <S.Wrapper>
      <div className="titles">
        <div className="action-bar">
          <div className="bar">
            <div className="assignment-container">
              <div className="vehicle-overview">
                {/* <Tags.ItemOverview
                  avatar={VehicleThumbnail}
                  avatarAlt="vehicle"
                  title={licensePlateNumber}
                  subtitle={model}
                /> */}
              </div>
              <Icon iconKey={IconKeys.ARROW_RIGHT} />
              <div className="driver-overview">
                {selectedDriver
                  ? (
                    <Tags.Driver
                      imageSrc={selectedDriver.avatarUrl || driverThumbnail}
                      name={
                        generatePreviewFullName(selectedDriver.firstName, selectedDriver.lastName)
                      }
                      licenseName={selectedDriver.licenseName as string}
                    />
                  )
                  : <div className="driver-placeholder"> Choose driver from below </div>}
              </div>
            </div>
            <div className="buttons-container">
              <Button
                className="cancel-btn"
                onClick={onCancel}
              >
                Cancel
              </Button>
              <Button
                className="confirm-btn"
                disabled={!selectedDriver}
                onClick={handleDriverAssignment}
              >
                Become authorized
              </Button>
            </div>
          </div>
          {!!assignError && <div className="error"> {assignError} </div>}
        </div>
        <div className="drivers-toolbar">
          <h3>{availableDrivers ? `${availableDrivers.length} available driver${availableDrivers.length > 1 ? 's' : ''}` : 'No available drivers'}</h3>
          {
            SEARCH_CONFIG.map((filterConfig: FilterConfig) => (
              <DropdownFilter
                key={filterConfig.type}
                config={filterConfig}
                filters={filters}
                setFilters={setFilters}
              />
            ))
          }
        </div>
      </div>
      <div className="drivers-list">
        {isLoading && <div> Loading... </div>}
        {!isLoading && availableDrivers && (
          <ul className="list-container">
            {!!availableDrivers.length
              && availableDrivers.map((item: DriverType) => (
                <Driver
                  driver={item}
                  key={item._id}
                  isChecked={selectedDriver?._id === item._id}
                  selectDriver={handleDriverSelection}
                />
              ))}
            {!isFetching && !availableDrivers.length && <div className="no-data">No drivers found</div>}
          </ul>
        )}
      </div>
      <div className="info">
        * Only drivers with face data can be added to list of authorized drivers. You can create learn face request if needed (device should be online).
      </div>
    </S.Wrapper>
  );
}

export default AddDriverToVehicle;
